import { useEffect, useState } from 'react';
import {
  useGetSivChecklistQuery,
  usePublishSIVChecklistToEtmfMutation,
  useSendForReviewMutation
} from 'src/services/SivChecklist';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { hideLoader, showLoader } from 'src/store/Loader';
import {
  ISIVChecklist,
  ISIVChecklistAdverseEventReporting,
  ISIVChecklistClinicalTrialData,
  ISIVChecklistConfirmPage,
  ISIVChecklistInformedConsentVerification,
  ISIVChecklistMonitoring,
  ISIVChecklistRequirementsforEcrfcompletion,
  ISIVChecklistSiteAdequacy,
  ISIVChecklistSourceDocumentation
} from 'src/types/SIVChecklist';
import { checkGetProperty } from 'src/util/STR';
import { useCalendarEventByVisitType } from 'src/util/useCalendarEventByVisitType';
import { updateUserAccess } from 'src/util/util';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import moment from 'moment';

export interface ISIVChecklistStep1Accordions {
  clinicalTrialDetails: boolean;
  siteAdequacy: boolean;
  informedConsentVerification: boolean;
}

export interface ISIVChecklistStep2Accordions {
  adverseEventReporting: boolean;
  requirementsforEcrfcompletion: boolean;
  sourceDocumentation: boolean;
  monitoring: boolean;
}

export interface ISIVChecklistStep3Accordions {
  confirmPage: boolean;
}

export const defaultCollapsed: ISIVChecklistStep1Accordions = {
  clinicalTrialDetails: false,
  informedConsentVerification: false,
  siteAdequacy: false
};

export const defaultStep2Collapsed: ISIVChecklistStep2Accordions = {
  adverseEventReporting: false,
  requirementsforEcrfcompletion: false,
  sourceDocumentation: false,
  monitoring: false
};

export interface IStep1SivChecklist {
  sivChecklistData?: ISIVChecklist;
}

export type ClinicalDataKeys = keyof ISIVChecklistClinicalTrialData;

export type SiteAdequacyKeys = keyof ISIVChecklistSiteAdequacy;

export type InformedConsentVerificationKeys = keyof ISIVChecklistInformedConsentVerification;

export type AdverseEventReportingKeys = keyof ISIVChecklistAdverseEventReporting;

export type RequirementsforEcrfcompletionKeys = keyof ISIVChecklistRequirementsforEcrfcompletion;

export type SourceDocumentationKeys = keyof ISIVChecklistSourceDocumentation;

export type MonitoringKeys = keyof ISIVChecklistMonitoring;

export type ConfirmPageKeys = keyof ISIVChecklistConfirmPage;

export const clinicalDataKeys: ClinicalDataKeys[] = [
  'siv_visit_date',
  'siv_checklist_protocol_version',
  'siv_checklist_artifact_num',
  'siv_checklist_protocol_version_num',
  'siv_checklist_study_objectives',
  'siv_checklist_inclusion_exclusion',
  'siv_checklist_study_design_timelines',
  'siv_checklist_soa',
  'siv_checklist_enrollment_procedure',
  'siv_checklist_lab_procedure',
  'siv_checklist_ip_da',
  'siv_checklist_protocol_deviation',
  'siv_checklist_other_procedures'
];
export const siteAdequacyKeys: SiteAdequacyKeys[] = [
  'siv_checklist_fics_list',
  'siv_checklist_contact_list',
  'siv_checklist_doa',
  'siv_checklist_siv_training',
  'siv_checklist_study_supplies',
  'siv_checklist_equipment'
];
export const informedConsentVerificationKeys: InformedConsentVerificationKeys[] = [
  'siv_checklist_ic_template',
  'siv_checklist_ic_translation',
  'siv_checklist_translation_service'
];

export const adverseEventReportingKeys: AdverseEventReportingKeys[] = ['siv_checklist_ib_isf'];

export const requirementsforEcrfcompletionKeys: RequirementsforEcrfcompletionKeys[] = ['siv_checklist_ecrf_guidelines'];

export const sourceDocumentationKeys: SourceDocumentationKeys[] = ['siv_checklist_source_doc_review'];

export const monitoringKeys: MonitoringKeys[] = ['siv_checklist_monitoring_plan'];

export const confirmPageKeys: ConfirmPageKeys[] = [
  'id',
  'siv_checklist_siv_performed',
  'siv_checklist_not_performed',
  'siv_checklist_print_name',
  'siv_checklist_signature',
  'siv_checklist_date',
  'reason'
];

type TypeStep1SIVChecklistData =
  | ISIVChecklistClinicalTrialData
  | ISIVChecklistSiteAdequacy
  | ISIVChecklistInformedConsentVerification
  | ISIVChecklistAdverseEventReporting
  | ISIVChecklistRequirementsforEcrfcompletion
  | ISIVChecklistMonitoring
  | ISIVChecklistSourceDocumentation
  | ISIVChecklistConfirmPage;

export interface IStep1SIVChecklistData {
  clinicalTrialDetails: Partial<ISIVChecklistClinicalTrialData>;
  siteAdequacy: Partial<ISIVChecklistSiteAdequacy>;
  informedConsentVerification: Partial<ISIVChecklistInformedConsentVerification>;
  adverseEventReporting: Partial<ISIVChecklistAdverseEventReporting>;
  requirementsforEcrfcompletion: Partial<ISIVChecklistRequirementsforEcrfcompletion>;
  sourceDocumentation: Partial<ISIVChecklistSourceDocumentation>;
  monitoring: Partial<ISIVChecklistMonitoring>;
  confirmPage: Partial<ISIVChecklistConfirmPage>;
}

type DataKeys = keyof IStep1SIVChecklistData;

export interface Step4 {
  isSubmit: boolean;
}

export interface ISIVChecklistModified {
  Step1: ISIVChecklistStep1Accordions;
  Step2: ISIVChecklistStep2Accordions;
  Step3: ISIVChecklistStep3Accordions;
}

type CheckListModified = ISIVChecklistStep1Accordions | ISIVChecklistStep2Accordions | ISIVChecklistStep3Accordions;

export type ISIVChecklistModifiedKeys = keyof ISIVChecklistModified;

export type ISIVChecklistModifiedKeysOfKeys =
  | keyof ISIVChecklistStep1Accordions
  | keyof ISIVChecklistStep2Accordions
  | keyof ISIVChecklistStep3Accordions
  | keyof Step4;

const defaultChecklistModified: ISIVChecklistModified = {
  Step1: { clinicalTrialDetails: false, informedConsentVerification: false, siteAdequacy: false },
  Step2: {
    adverseEventReporting: false,
    monitoring: false,
    requirementsforEcrfcompletion: false,
    sourceDocumentation: false
  },
  Step3: { confirmPage: false }
};

export interface IClinicalTrialMandatory {
  siv_checklist_protocol_version: boolean;
  siv_checklist_study_objectives: boolean;
  siv_checklist_inclusion_exclusion: boolean;
  siv_checklist_study_design_timelines: boolean;
  siv_checklist_soa: boolean;
  siv_checklist_enrollment_procedure: boolean;
  siv_checklist_lab_procedure: boolean;
  siv_checklist_ip_da: boolean;
  siv_checklist_protocol_deviation: boolean;
  siv_checklist_other_procedures: boolean;
}
export interface ISiteAdequacyMandatory {
  siv_checklist_fics_list: boolean;
  siv_checklist_contact_list: boolean;
  siv_checklist_doa: boolean;
  siv_checklist_siv_training: boolean;
  siv_checklist_study_supplies: boolean;
}

export interface InformedConsentVerificationMandatory {
  siv_checklist_ic_template: boolean;
  siv_checklist_ic_translation: boolean;
  siv_checklist_translation_service: boolean;
}
export interface IAdverseEventReportingMandatory {
  siv_checklist_ib_isf: boolean;
}
export interface IRequirementsforEcrfcompletionMandatory {
  siv_checklist_ecrf_guidelines: boolean;
}
export interface ISourceDocumentationMandatory {
  siv_checklist_source_doc_review: boolean;
}

export interface IMonitoringMandatory {
  siv_checklist_monitoring_plan: boolean;
}
export interface IConfirmPageMandatory {
  siv_checklist_siv_performed: boolean;
  siv_checklist_not_performed: boolean;
}

export interface ISIVChecklistMandatory {
  clinicalTrialDetails: IClinicalTrialMandatory;
  siteAdequacy: ISiteAdequacyMandatory;
  informedConsentVerification: InformedConsentVerificationMandatory;
  adverseEventReporting: IAdverseEventReportingMandatory;
  requirementsforEcrfcompletion: IRequirementsforEcrfcompletionMandatory;
  sourceDocumentation: ISourceDocumentationMandatory;
  monitoring: IMonitoringMandatory;
  confirmPage: IConfirmPageMandatory;
}

export type MandatoryData =
  | IClinicalTrialMandatory
  | ISiteAdequacyMandatory
  | InformedConsentVerificationMandatory
  | IAdverseEventReportingMandatory
  | IRequirementsforEcrfcompletionMandatory
  | ISourceDocumentationMandatory
  | IMonitoringMandatory
  | IConfirmPageMandatory;

export type SIVCheckListMandatoryKeys = keyof ISIVChecklistMandatory;

export const defaultMandatory: ISIVChecklistMandatory = {
  clinicalTrialDetails: {
    siv_checklist_protocol_version: false,
    siv_checklist_study_objectives: false,
    siv_checklist_inclusion_exclusion: false,
    siv_checklist_study_design_timelines: false,
    siv_checklist_soa: false,
    siv_checklist_enrollment_procedure: false,
    siv_checklist_lab_procedure: false,
    siv_checklist_ip_da: false,
    siv_checklist_protocol_deviation: false,
    siv_checklist_other_procedures: false
  },
  siteAdequacy: {
    siv_checklist_fics_list: false,
    siv_checklist_contact_list: false,
    siv_checklist_doa: false,
    siv_checklist_siv_training: false,
    siv_checklist_study_supplies: false
  },
  informedConsentVerification: {
    siv_checklist_ic_template: false,
    siv_checklist_ic_translation: false,
    siv_checklist_translation_service: false
  },
  adverseEventReporting: {
    siv_checklist_ib_isf: false
  },
  requirementsforEcrfcompletion: {
    siv_checklist_ecrf_guidelines: false
  },
  sourceDocumentation: {
    siv_checklist_source_doc_review: false
  },
  monitoring: {
    siv_checklist_monitoring_plan: false
  },
  confirmPage: {
    siv_checklist_siv_performed: false,
    siv_checklist_not_performed: false
  }
};

export const useStep1SivChecklist = () => {
  const dispatch = useAppDispatch();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const selectedRowData: any = useAppSelector(state => state.sivCheckList.selectedRowData);

  const { data, error, isError, isLoading, refetch } = useGetSivChecklistQuery({
    monitoring_visit_id: selectedRowData.id,
    site_id: selectedSite,
    study_id: studyId,
    country: selectedCountry,
    application: process.env.REACT_APP_NAME
  });

  const [
    publishSivChecklistToEtmf,
    {
      isLoading: publishSivChecklistLoading,
      isSuccess: publishSivChecklistSuccess,
      data: publishSivCheckListData,
      isError: publishSivChecklistIsError,
      error: publishSivChecklistError
    }
  ] = usePublishSIVChecklistToEtmfMutation();

  const [
    sendForReviewFunc,
    {
      isLoading: sendForReviewIsLoading,
      isSuccess: sendForReviewIsSuccess,
      data: sendForReviewData,
      isError: sendForReviewIsError,
      error: sendForReviewError
    }
  ] = useSendForReviewMutation();

  const [mandatory, setMandatory] = useState<ISIVChecklistMandatory>(defaultMandatory);

  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [step1SivChecklist, setStep1SivChecklist] = useState<ISIVChecklistStep1Accordions>({
    ...defaultCollapsed,
    clinicalTrialDetails: true
  });

  const [step2SivChecklist, setStep2SivChecklist] = useState<ISIVChecklistStep2Accordions>({
    ...defaultStep2Collapsed,
    adverseEventReporting: true
  });

  const [step3SivChecklist, setStep3SivChecklist] = useState<ISIVChecklistStep3Accordions>({
    ...defaultStep2Collapsed,
    confirmPage: true
  });

  const [sivChecklistModified, setSIVChecklistModified] = useState<ISIVChecklistModified>(defaultChecklistModified);

  const [saveDisable, setSaveDisable] = useState(!data?.is_published && data?.siv_checklist_signature ? true : false);
  const [isUpdate, setIsUpdate] = useState(data?.study_id ? true : false);

  const getValues = <T, K extends keyof T>(dta: ISIVChecklist, keys: K[]): T => {
    const newData: Partial<T> = {};
    keys.forEach(key => {
      const val: any = dta[key as keyof ISIVChecklist] || '';
      newData[key] = val;
    });
    return newData as T;
  };
  const [sivChecklistData, setSIVChecklistData] = useState<Partial<IStep1SIVChecklistData>>({});

  const [sivChecklistModifiedData, setSIVChecklistModifiedData] = useState<Partial<IStep1SIVChecklistData>>({});

  useEffect(() => {
    updateUserAccess(error, dispatch);
  }, [error]);

  useEffect(() => {
    if (isLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
    return () => {
      dispatch(hideLoader());
    };
  }, [isLoading]);

  const getEquipments = (str: string): string[] => {
    return str.split(',').filter(i => i.length > 0);
  };

  const { getMonitoringEventData } = useCalendarEventByVisitType('Site Initiation Visit');
  const { userPreferedDateFormat } = usePreferedDateFormat();
  useEffect(() => {
    if (!isLoading) {
      (async () => {
        const sivEventStartDate = await getMonitoringEventData();
        const sivData: ISIVChecklist = data && !isError ? data : ({} as ISIVChecklist);
        const clinical = getValues<ISIVChecklistClinicalTrialData, ClinicalDataKeys>(sivData, clinicalDataKeys);
        const site = getValues<ISIVChecklistSiteAdequacy, SiteAdequacyKeys>(sivData, siteAdequacyKeys);
        site.siv_checklist_equipment = getEquipments(site.siv_checklist_equipment?.toString());
        const informedConsent = getValues<ISIVChecklistInformedConsentVerification, InformedConsentVerificationKeys>(
          sivData,
          informedConsentVerificationKeys
        );
        const adverseEvent = getValues<ISIVChecklistAdverseEventReporting, AdverseEventReportingKeys>(
          sivData,
          adverseEventReportingKeys
        );
        const requirementsforEcr = getValues<
          ISIVChecklistRequirementsforEcrfcompletion,
          RequirementsforEcrfcompletionKeys
        >(sivData, requirementsforEcrfcompletionKeys);
        const sourceDocu = getValues<ISIVChecklistSourceDocumentation, SourceDocumentationKeys>(
          sivData,
          sourceDocumentationKeys
        );
        const monitor = getValues<ISIVChecklistMonitoring, MonitoringKeys>(sivData, monitoringKeys);
        const confirm = getValues<ISIVChecklistConfirmPage, ConfirmPageKeys>(sivData, confirmPageKeys);
        const state: Partial<IStep1SIVChecklistData> = {
          clinicalTrialDetails: {
            ...clinical,
            siv_visit_date: sivEventStartDate
              ? moment(new Date(`${sivEventStartDate}Z`)).format(userPreferedDateFormat.toUpperCase())
              : ''
          },
          siteAdequacy: site,
          informedConsentVerification: informedConsent,
          adverseEventReporting: adverseEvent,
          requirementsforEcrfcompletion: requirementsforEcr,
          sourceDocumentation: sourceDocu,
          monitoring: monitor,
          confirmPage: confirm
        };
        Object.entries(sivChecklistModifiedData).forEach(item => {
          state[item[0] as DataKeys] = item[1];
        });
        setSIVChecklistData(state);
        setSIVChecklistModifiedData({});
        if (data) {
          setIsUpdate(data?.study_id ? true : false);
          !data?.is_published && data?.siv_checklist_signature ? setSaveDisable(true) : setSaveDisable(false);
        }
      })();
    }
  }, [data, isLoading, isError]);
  const toggle = (key: keyof ISIVChecklistStep1Accordions, expanded: boolean) => {
    if (!expanded) {
      setStep1SivChecklist(prev => ({ ...prev, [key]: expanded }));
    } else {
      setStep1SivChecklist({ ...defaultCollapsed, [key]: expanded });
    }
  };

  const toggleStep2 = (key: keyof ISIVChecklistStep2Accordions, expanded: boolean) => {
    if (!expanded) {
      setStep2SivChecklist(prev => ({ ...prev, [key]: expanded }));
    } else {
      setStep2SivChecklist({ ...defaultStep2Collapsed, [key]: expanded });
    }
  };

  const toggleStep3 = (key: keyof ISIVChecklistStep3Accordions, expanded: boolean) => {
    setStep3SivChecklist(prev => ({ ...prev, [key]: expanded }));
  };

  const updateSIVChecklistMandatoryData = (key: keyof ISIVChecklistMandatory, value: MandatoryData) => {
    setMandatory(prev => ({ ...prev, [key]: value }));
  };

  const updateStep1SIVChecklistData = (key: keyof IStep1SIVChecklistData, value: TypeStep1SIVChecklistData) => {
    setSIVChecklistData(prev => ({ ...prev, [key]: value }));
  };

  const updateClinicalData = (dta: ISIVChecklistClinicalTrialData) => {
    setSIVChecklistModified(prev => ({ ...prev, Step1: { ...prev.Step1, clinicalTrialDetails: true } }));
    updateStep1SIVChecklistData('clinicalTrialDetails', dta);
  };

  const updateSiteAdequacyData = (dta: ISIVChecklistSiteAdequacy) => {
    setSIVChecklistModified(prev => ({ ...prev, Step1: { ...prev.Step1, siteAdequacy: true } }));
    updateStep1SIVChecklistData('siteAdequacy', dta);
  };

  const updateIinformedConsentVerification = (dta: ISIVChecklistInformedConsentVerification) => {
    setSIVChecklistModified(prev => ({ ...prev, Step1: { ...prev.Step1, informedConsentVerification: true } }));
    updateStep1SIVChecklistData('informedConsentVerification', dta);
  };

  const updateAdverseEventReporting = (dta: ISIVChecklistAdverseEventReporting) => {
    setSIVChecklistModified(prev => ({ ...prev, Step2: { ...prev.Step2, adverseEventReporting: true } }));
    updateStep1SIVChecklistData('adverseEventReporting', dta);
  };

  const updateRequirementsforEcrfCompletion = (dta: ISIVChecklistRequirementsforEcrfcompletion) => {
    setSIVChecklistModified(prev => ({ ...prev, Step2: { ...prev.Step2, requirementsforEcrfcompletion: true } }));
    updateStep1SIVChecklistData('requirementsforEcrfcompletion', dta);
  };

  const updateSourceDocumentation = (dta: ISIVChecklistSourceDocumentation) => {
    setSIVChecklistModified(prev => ({ ...prev, Step2: { ...prev.Step2, sourceDocumentation: true } }));
    updateStep1SIVChecklistData('sourceDocumentation', dta);
  };
  const updateMonitoring = (dta: ISIVChecklistMonitoring) => {
    setSIVChecklistModified(prev => ({ ...prev, Step2: { ...prev.Step2, monitoring: true } }));
    updateStep1SIVChecklistData('monitoring', dta);
  };
  const updateConfirmPage = (dta: ISIVChecklistConfirmPage) => {
    setSIVChecklistModified(prev => ({ ...prev, Step3: { ...prev.Step3, confirmPage: true } }));
    updateStep1SIVChecklistData('confirmPage', dta);
  };

  const getData = <T, K extends keyof T>(dta: T, key: K): T[K] => {
    return (dta && dta[key] ? dta[key] : {}) as T[K];
  };

  const setIsChange = (step: ISIVChecklistModifiedKeys, key: ISIVChecklistModifiedKeysOfKeys, val: boolean): void => {
    setSIVChecklistModified(prev => ({ ...prev, [step]: { ...prev[step], [key]: val } }));
  };

  const keys = Object.keys(sivChecklistData).length;

  const isModified = (obj: CheckListModified): boolean => Object.entries(obj).some(item => item[1] === true);

  const getModifiedKey = (obj: any, val: any) => Object.keys(obj).find(key => obj[key as keyof any] === val);

  const getModifiedKeys = (obj: any, val: any) => Object.keys(obj).filter(key => obj[key as keyof any] === val);

  const getSivCheckListData = (key: ISIVChecklistModifiedKeysOfKeys, _: boolean) => {
    const step1Keys = getModifiedKeys(sivChecklistModified.Step1, true);
    const step2Keys = getModifiedKeys(sivChecklistModified.Step2, true);
    const step3Keys = getModifiedKeys(sivChecklistModified.Step3, true);
    const allModifiedKeys = [...step1Keys, ...step2Keys, ...step3Keys].filter(item => item !== key);
    const modifiedData: Partial<IStep1SIVChecklistData> = {};
    allModifiedKeys.forEach(modifiedKey => {
      modifiedData[modifiedKey as DataKeys] = sivChecklistData[modifiedKey as DataKeys];
    });
    setSIVChecklistModifiedData(modifiedData);
    refetch();
  };

  const isPublished = data?.is_published ? true : false;
  const notifyUser = data?.notify_user;

  const checkChanged = (move: boolean): boolean => {
    const step1 = isModified(sivChecklistModified.Step1);
    const step2 = isModified(sivChecklistModified.Step2);
    const step3 = isModified(sivChecklistModified.Step3);
    if (move && (step1 || step2 || step3)) {
      const step1Key = getModifiedKey(sivChecklistModified.Step1, true) as keyof ISIVChecklistStep1Accordions;
      const step2Key = getModifiedKey(sivChecklistModified.Step2, true) as keyof ISIVChecklistStep2Accordions;
      const step3Key = getModifiedKey(sivChecklistModified.Step3, true) as keyof ISIVChecklistStep3Accordions;
      const page = step1 ? 0 : step2 ? 1 : step3 ? 2 : activeStep;
      if (step1Key) {
        toggle(step1Key, true);
      } else if (step2Key) {
        toggleStep2(step2Key, true);
      } else if (step3Key) {
        toggleStep3(step3Key, true);
      }
      setActiveStep(page);
      // if(data?.is_published)
      // SignatureBreaking()
    }
    return step1 || step2 || step3;
  };

  const checkMandatoryFields = (): boolean => {
    // Step 1 clinicalTrialDetails siteAdequacy informedConsentVerification
    const clinicalTrialDetailsKeys = getModifiedKeys(mandatory.clinicalTrialDetails, true);
    const siteAdequacyKeys = getModifiedKeys(mandatory.siteAdequacy, true);
    const informedConsentVerificationKeys = getModifiedKeys(mandatory.informedConsentVerification, true);
    // Step 2 adverseEventReporting requirementsforEcrfcompletion sourceDocumentation monitoring
    const adverseEventReportingKeys = getModifiedKeys(mandatory.adverseEventReporting, true);
    const requirementsforEcrfcompletionKeys = getModifiedKeys(mandatory.requirementsforEcrfcompletion, true);
    const sourceDocumentationKeys = getModifiedKeys(mandatory.sourceDocumentation, true);
    const monitoringKeys = getModifiedKeys(mandatory.monitoring, true);
    //Step 3   confirmPage;
    const confirmPageKeys = getModifiedKeys(mandatory.confirmPage, true);

    return [
      ...clinicalTrialDetailsKeys,
      ...siteAdequacyKeys,
      ...informedConsentVerificationKeys,
      ...adverseEventReportingKeys,
      ...requirementsforEcrfcompletionKeys,
      ...sourceDocumentationKeys,
      ...monitoringKeys,
      ...confirmPageKeys
    ].length === 0
      ? false
      : true;
  };

  const validateEmpty = (obj: any, keys: string[]): boolean => {
    return keys.every(key => {
      try {
        const val: string | any = checkGetProperty(obj, key);
        if (val && val !== '') {
          return true;
        }
      } catch (e) {
        console.error(e);
      }
      return false;
    });
  };

  const validate = (obj: any, keys: string[]): boolean => {
    return keys.every(key => {
      try {
        const val: string | any = checkGetProperty(obj, key);
        // const comments: string | any = checkGetProperty(obj, `${key}_comm`);
        if (val && val !== '') {
          if (val === 'Y' || val === 'N') {
            return true;
          } else if (val === 'NA') {
            // PICTMS-3001 - When Yes in Site Adequacy, adding values as NA to bypass error
            return true;
          }
        }
      } catch (e) {
        console.error(e);
        return false;
      }
    });
  };

  const validateclinicalTrialDetails = () => {
    return validate(sivChecklistData.clinicalTrialDetails, Object.keys(mandatory.clinicalTrialDetails));
  };

  const validatesiteAdequacy = () => {
    return validate(sivChecklistData.siteAdequacy, Object.keys(mandatory.siteAdequacy));
  };

  const validateinformedConsentVerification = () => {
    return validate(sivChecklistData.informedConsentVerification, Object.keys(mandatory.informedConsentVerification));
  };

  const validateadverseEventReporting = () => {
    return validate(sivChecklistData.adverseEventReporting, Object.keys(mandatory.adverseEventReporting));
  };

  const validaterequirementsforEcrfcompletion = () => {
    return validate(
      sivChecklistData.requirementsforEcrfcompletion,
      Object.keys(mandatory.requirementsforEcrfcompletion)
    );
  };

  const validatesourceDocumentation = () => {
    return validate(sivChecklistData.sourceDocumentation, Object.keys(mandatory.sourceDocumentation));
  };

  const validatemonitoring = () => {
    return validate(sivChecklistData.monitoring, Object.keys(mandatory.monitoring));
  };

  const validateconfirmPage = () => {
    return (
      validate(sivChecklistData.confirmPage, Object.keys(mandatory.confirmPage)) &&
      validateEmpty(sivChecklistData.confirmPage, ['siv_checklist_signature', 'siv_checklist_date'])
    );
  };

  const isChanged = checkChanged(false);

  const validateMandatoryFields = (move: boolean) => {
    const clinicalTrialDetails = validate(
      sivChecklistData.clinicalTrialDetails,
      Object.keys(mandatory.clinicalTrialDetails)
    );
    const siteAdequacy = validate(sivChecklistData.siteAdequacy, Object.keys(mandatory.siteAdequacy));

    const informedConsentVerification = validate(
      sivChecklistData.informedConsentVerification,
      Object.keys(mandatory.informedConsentVerification)
    );
    const step1Key = !clinicalTrialDetails
      ? 'clinicalTrialDetails'
      : !siteAdequacy
      ? 'siteAdequacy'
      : !informedConsentVerification
      ? 'informedConsentVerification'
      : null;
    if (step1Key) {
      if (move) {
        toggle(step1Key, true);
        setActiveStep(0);
      }
      return false;
    }
    const adverseEventReporting = validate(
      sivChecklistData.adverseEventReporting,
      Object.keys(mandatory.adverseEventReporting)
    );
    const requirementsforEcrfcompletion = validate(
      sivChecklistData.requirementsforEcrfcompletion,
      Object.keys(mandatory.requirementsforEcrfcompletion)
    );
    const sourceDocumentation = validate(
      sivChecklistData.sourceDocumentation,
      Object.keys(mandatory.sourceDocumentation)
    );
    const monitoring = validate(sivChecklistData.monitoring, Object.keys(mandatory.monitoring));
    const step2Key = !adverseEventReporting
      ? 'adverseEventReporting'
      : !requirementsforEcrfcompletion
      ? 'requirementsforEcrfcompletion'
      : !sourceDocumentation
      ? 'sourceDocumentation'
      : !monitoring
      ? 'monitoring'
      : null;
    if (step2Key) {
      if (move) {
        toggleStep2(step2Key, true);
        setActiveStep(1);
      }
      return false;
    }
    const confirmPage = validate(sivChecklistData.confirmPage, Object.keys(mandatory.confirmPage));
    if (!confirmPage) {
      if (move) {
        toggleStep3('confirmPage', true);
        setActiveStep(2);
      }
      return false;
    }
    return true;
  };
  const validateMandatoryFieldsForSign = (move: boolean) => {
    const clinicalTrialDetails = validate(
      sivChecklistData.clinicalTrialDetails,
      Object.keys(mandatory.clinicalTrialDetails)
    );
    const siteAdequacy = validate(sivChecklistData.siteAdequacy, Object.keys(mandatory.siteAdequacy));
    const informedConsentVerification = validate(
      sivChecklistData.informedConsentVerification,
      Object.keys(mandatory.informedConsentVerification)
    );
    const step1Key = !clinicalTrialDetails
      ? 'clinicalTrialDetails'
      : !siteAdequacy
      ? 'siteAdequacy'
      : !informedConsentVerification
      ? 'informedConsentVerification'
      : null;
    if (step1Key) {
      if (move) {
        toggle(step1Key, true);
        setActiveStep(0);
      }
      return false;
    }
    const adverseEventReporting = validate(
      sivChecklistData.adverseEventReporting,
      Object.keys(mandatory.adverseEventReporting)
    );
    const requirementsforEcrfcompletion = validate(
      sivChecklistData.requirementsforEcrfcompletion,
      Object.keys(mandatory.requirementsforEcrfcompletion)
    );
    const sourceDocumentation = validate(
      sivChecklistData.sourceDocumentation,
      Object.keys(mandatory.sourceDocumentation)
    );
    const monitoring = validate(sivChecklistData.monitoring, Object.keys(mandatory.monitoring));
    const step2Key = !adverseEventReporting
      ? 'adverseEventReporting'
      : !requirementsforEcrfcompletion
      ? 'requirementsforEcrfcompletion'
      : !sourceDocumentation
      ? 'sourceDocumentation'
      : !monitoring
      ? 'monitoring'
      : null;
    if (step2Key) {
      if (move) {
        toggleStep2(step2Key, true);
        setActiveStep(1);
      }
      return false;
    }
    const confirmPage = validate(sivChecklistData.confirmPage, Object.keys(mandatory.confirmPage));
    if (!confirmPage) {
      if (move) {
        toggleStep3('confirmPage', true);
        setActiveStep(2);
      }
      return false;
    }
    return true;
  };

  const isMandatoryFields = checkMandatoryFields();
  const validateMandatory = validateMandatoryFields(false);
  return {
    keys,
    step1SivChecklist,
    toggle,
    step2SivChecklist,
    toggleStep2,
    step3SivChecklist,
    toggleStep3,
    isUpdate,
    setIsUpdate,
    clinicalTrialData: getData(sivChecklistData, 'clinicalTrialDetails') as ISIVChecklistClinicalTrialData,
    siteAdequacyData: getData(sivChecklistData, 'siteAdequacy') as ISIVChecklistSiteAdequacy,
    informedConsentVerificationData: getData(
      sivChecklistData,
      'informedConsentVerification'
    ) as ISIVChecklistInformedConsentVerification,
    adverseEventReportingData: getData(sivChecklistData, 'adverseEventReporting') as ISIVChecklistAdverseEventReporting,
    requirementsforEcrfCompletionData: getData(
      sivChecklistData,
      'requirementsforEcrfcompletion'
    ) as ISIVChecklistRequirementsforEcrfcompletion,
    sourceDocumentationData: getData(sivChecklistData, 'sourceDocumentation') as ISIVChecklistSourceDocumentation,
    monitoringData: getData(sivChecklistData, 'monitoring') as ISIVChecklistMonitoring,
    confirmPageData: getData(sivChecklistData, 'confirmPage') as ISIVChecklistConfirmPage,
    updateSiteAdequacyData,
    updateIinformedConsentVerification,
    updateClinicalData,
    updateAdverseEventReporting,
    updateRequirementsforEcrfCompletion,
    updateSourceDocumentation,
    updateMonitoring,
    updateConfirmPage,
    setSaveDisable,
    saveDisable,
    setIsChange,
    sivChecklistModified,
    activeStep,
    setActiveStep,
    skipped,
    setSkipped,
    selectedSite,
    studyId,
    selectedCountry,
    getSivCheckListData,
    checkChanged,
    isChanged,
    updateSIVChecklistMandatoryData,
    mandatory,
    isMandatoryFields,
    validateMandatory,
    validateMandatoryFields,
    validateclinicalTrialDetails,
    validatesiteAdequacy,
    validateinformedConsentVerification,
    validateadverseEventReporting,
    validaterequirementsforEcrfcompletion,
    validatesourceDocumentation,
    validatemonitoring,
    validateconfirmPage,
    validateMandatoryFieldsForSign,
    publishSivChecklistToEtmf,
    publishSivChecklistLoading,
    publishSivChecklistSuccess,
    publishSivCheckListData,
    publishSivChecklistIsError,
    publishSivChecklistError,
    isPublished,
    sendForReviewFunc,
    sendForReviewIsLoading,
    sendForReviewIsSuccess,
    sendForReviewData,
    sendForReviewIsError,
    sendForReviewError,
    notifyUser
  };
};
